
class CdiGallery {
  constructor(galleryEl) {
    this.gallery = galleryEl;

    this.slides = [];
    this._navEl = null;
    this._counterEl = null;
    this._observers = [];
    this._abortController = new AbortController();
    if (this.gallery.elements && this.gallery.elements.viewer) {
      this._init();
    } else {
      requestAnimationFrame(() => this._init());
    }
  }
  _init() {
    this._buildSlides();
    this._bindMainArrows();
    this._renderNav();
    this._renderCounter();
    this._syncAll(this._getActiveIndex());
  }
  destroy() {
    this._abortController.abort();
    this._observers.forEach((obs) => obs.disconnect());
    this._observers = [];
    if (this._navEl) {
      this._navEl.remove();
      this._navEl = null;
    }
    if (this._counterEl) {
      this._counterEl.remove();
      this._counterEl = null;
    }
    this.slides = [];
  }
  _buildSlides() {
    this.slides = [];
    const viewer = this.gallery.elements?.viewer;
    if (!viewer) return;
    viewer.querySelectorAll('li[data-media-id]').forEach((el) => {
      this.slides.push({ slideEl: el, mediaId: el.dataset.mediaId });
    });
  }
  _getActiveIndex() {
    const viewer = this.gallery.elements?.viewer;
    if (!viewer) return 0;
    const active = viewer.querySelector('li.is-active[data-media-id]');
    if (!active) return 0;
    const idx = this.slides.findIndex((s) => s.mediaId === active.dataset.mediaId);
    return idx >= 0 ? idx : 0;
  }
  navigateTo(idx) {
    if (idx < 0 || idx >= this.slides.length) return;
    const fullMediaId = this.slides[idx].mediaId;
    if (!fullMediaId) return;
    this.gallery.setActiveMedia(fullMediaId, false);
    this._syncAll(idx);
  }
  _bindMainArrows() {
    const viewer = this.gallery.elements?.viewer;
    if (!viewer) return;

    const prevBtn = viewer.querySelector('button[name="previous"]');
    const nextBtn = viewer.querySelector('button[name="next"]');
    if (!prevBtn || !nextBtn) return;

    const signal = this._abortController.signal;
    const updateArrowState = (idx) => {
      prevBtn.toggleAttribute('disabled', idx <= 0);
      nextBtn.toggleAttribute('disabled', idx >= this.slides.length - 1);
    };
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const idx = this._getActiveIndex();
      this.navigateTo(idx - 1);
      updateArrowState(idx - 1);
    }, { capture: true, signal });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const idx = this._getActiveIndex();
      this.navigateTo(idx + 1);
      updateArrowState(idx + 1);
    }, { capture: true, signal });
    updateArrowState(this._getActiveIndex());
    const arrowObserver = new MutationObserver(() => {
      updateArrowState(this._getActiveIndex());
    });
    this.slides.forEach(({ slideEl }) => {
      arrowObserver.observe(slideEl, { attributes: true, attributeFilter: ['class'] });
    });
    this._observers.push(arrowObserver);
    viewer.addEventListener('slideChanged', (e) => {
      const currentEl = e.detail?.currentElement;
      if (!currentEl) return;
      const mediaId = currentEl.dataset?.mediaId;
      if (!mediaId) return;
      const idx = this.slides.findIndex((s) => s.mediaId === mediaId);
      if (idx >= 0) {
        this._syncAll(idx);
        updateArrowState(idx);
      }
    }, { signal });
  }
  _renderNav() {
    if (this.slides.length <= 1) return;
    const old = this.gallery.querySelector('.cdi-gallery-nav');
    if (old) old.remove();
    const count = this.slides.length;
    const overflow = count > 10;
    const nav = document.createElement('div');
    nav.className = 'cdi-gallery-nav';
    nav.dataset.count = String(Math.min(count, 10));
    if (overflow) nav.dataset.overflow = 'true';
    const signal = this._abortController.signal;
    const bar = document.createElement('div');
    bar.className = 'cdi-gallery-nav__bar';
    for (let i = 0; i < count; i++) {
      const seg = document.createElement('button');
      seg.className = 'cdi-gallery-nav__seg';
      seg.dataset.idx = String(i);
      seg.setAttribute('aria-label', `Go to image ${i + 1}`);
      seg.addEventListener('click', () => this.navigateTo(i), { signal });
      bar.appendChild(seg);
    }
    const thumbs = document.createElement('div');
    thumbs.className = 'cdi-gallery-nav__thumbs';
    this.slides.forEach(({ slideEl }, i) => {
      const img = slideEl.querySelector('img');
      const thumb = document.createElement('button');
      thumb.className = 'cdi-gallery-nav__thumb';
      thumb.dataset.idx = String(i);
      thumb.setAttribute('aria-label', `Go to image ${i + 1}`);
      thumb.addEventListener('click', () => this.navigateTo(i), { signal });
      if (img) {
        const thumbImg = document.createElement('img');
        thumbImg.src = img.currentSrc || img.src;
        thumbImg.alt = img.alt || '';
        thumbImg.loading = 'lazy';
        thumb.appendChild(thumbImg);
      }
      thumbs.appendChild(thumb);
    });
    nav.appendChild(bar);
    nav.appendChild(thumbs);
    const sliderWrapper = this.gallery.querySelector('.slider-mobile-gutter');
    if (sliderWrapper) {
      sliderWrapper.appendChild(nav);
    } else {
      this.gallery.appendChild(nav);
    }
    this._navEl = nav;
  }
  _renderCounter() {
    if (this.slides.length <= 1) return;
    const old = this.gallery.querySelector('.cdi-gallery-counter');
    if (old) old.remove();
    const counter = document.createElement('div');
    counter.className = 'cdi-gallery-counter';
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');
    const sliderWrapper = this.gallery.querySelector('.slider-mobile-gutter');
    if (sliderWrapper) {
      sliderWrapper.appendChild(counter);
    } else {
      this.gallery.appendChild(counter);
    }
    this._counterEl = counter;
  }
  _syncAll(idx) {
    this._syncNav(idx);
    this._syncCounter(idx);
  }
  _syncNav(idx) {
    if (!this._navEl) return;
    this._navEl.querySelectorAll('.cdi-gallery-nav__seg').forEach((seg) => {
      seg.classList.toggle('is-active', Number(seg.dataset.idx) === idx);
    });

    this._navEl.querySelectorAll('.cdi-gallery-nav__thumb').forEach((thumb) => {
      thumb.classList.toggle('is-active', Number(thumb.dataset.idx) === idx);
    });

    if (this._navEl.dataset.overflow === 'true') {
      const activeThumb = this._navEl.querySelector(`.cdi-gallery-nav__thumb[data-idx="${idx}"]`);
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }
  _syncCounter(idx) {
    if (!this._counterEl) return;
    this._counterEl.textContent = `${idx + 1} / ${this.slides.length}`;
  }
}
class CdiPhotoSwipe {

  constructor(galleryEl, cdiGallery) {
    this.galleryEl = galleryEl;
    this.cdiGallery = cdiGallery;
    this._pswpModule = null;
    this._bindOpeners();
  }
  _bindOpeners() {
    const sectionEl = this.galleryEl.closest('[data-section]');
    const searchRoot = sectionEl || document;
    searchRoot.querySelectorAll('modal-opener').forEach((opener) => {
      opener.addEventListener('click', (e) => {
        const btn = e.target.closest('button.product__media-toggle');
        if (btn) this._onOpenerClick(e, opener, btn);
      }, true);
    });
  }
  _buildItems() {
    return this.cdiGallery.slides
      .map(({ slideEl, mediaId }) => {
        const img = slideEl.querySelector('img[data-pswp-width]');
        if (!img) return null;
        const src = img.src.replace(/(_\d+x\d*|_\d*x\d+)(\.(?:jpg|jpeg|png|webp))/, '$2')
                          || img.src;

        return {
          src,
          width: parseInt(img.dataset.pswpWidth, 10) || img.naturalWidth || 1200,
          height: parseInt(img.dataset.pswpHeight, 10) || img.naturalHeight || 1200,
          thumbEl: img,
          msrc: img.currentSrc || img.src,
          mediaId,
        };
      })
      .filter(Boolean);
  }
  _indexForButton(btn) {
    if (!btn) return 0;
    const pureId = btn.dataset.mediaId;
    const idx = this.cdiGallery.slides.findIndex(
      (s) => s.mediaId === pureId || s.mediaId.endsWith('-' + pureId)
    );
    return idx >= 0 ? idx : 0;
  }

  async _loadPhotoSwipe() {
    if (this._pswpModule) return this._pswpModule;
    for (let i = 0; i < 40; i++) {
      if (window.PhotoSwipeLightbox && window.PhotoSwipe) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!window.PhotoSwipeLightbox || !window.PhotoSwipe) {
      throw new Error('[CDI] PhotoSwipe globals not set — module script may have failed');
    }
    this._pswpModule = {
      PhotoSwipeLightbox: window.PhotoSwipeLightbox,
      PhotoSwipe: window.PhotoSwipe,
    };
    return this._pswpModule;
  }
  async _onOpenerClick(e, opener, btn) {
    if (opener.classList.contains('product__modal-opener--video') ||
        opener.classList.contains('product__modal-opener--model') ||
        opener.classList.contains('product__modal-opener--external_video')) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    const items = this._buildItems();
    if (items.length === 0) return;
    const startIndex = this._indexForButton(btn);
    let pswpMod;
    try {
      pswpMod = await this._loadPhotoSwipe();
    } catch (err) {
      console.warn('[CDI] PhotoSwipe load failed, falling back to Dawn modal', err);
      return;
    }
    const { PhotoSwipeLightbox, PhotoSwipe } = pswpMod;
    const lightbox = new PhotoSwipeLightbox({
      dataSource: items,
      index: startIndex,
      pswpModule: PhotoSwipe,
      showHideAnimationType: 'zoom',

      bgOpacity: 0.94,

      closeOnVerticalDrag: true,
      counterEl: true,
      arrowPrevEl: true,
      arrowNextEl: true,
      closeEl: true,

      paddingFn: (viewportSize) => {
        const mobile = viewportSize.x < 700;
        return {
          top: mobile ? 16 : 40,
          bottom: mobile ? 16 : 40,
          left: mobile ? 8 : 56,
          right: mobile ? 8 : 56,
        };
      },
    });
    const savedScrollY = window.scrollY;
    lightbox.on('change', () => {
      this.cdiGallery._syncAll(lightbox.pswp.currIndex);
    });
    lightbox.on('close', () => {
      const finalIdx = lightbox.pswp.currIndex;
      this.cdiGallery.navigateTo(finalIdx);
    });

    lightbox.on('destroy', () => {
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      const correctScroll = () => {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
      };
      window.addEventListener('scroll', correctScroll, { once: true, passive: true, capture: true });

      setTimeout(() => {
        window.removeEventListener('scroll', correctScroll, { capture: true });
      }, 300);
    });
    lightbox.init();
    lightbox.loadAndOpen(startIndex);
  }
}
function initCdiGallery() {
  document.querySelectorAll('media-gallery').forEach((galleryEl) => {
    if (galleryEl._cdiGalleryInit) return;
    galleryEl._cdiGalleryInit = true;
    const cdiGallery = new CdiGallery(galleryEl);
    const cdiPhotoSwipe = new CdiPhotoSwipe(galleryEl, cdiGallery);
    galleryEl._cdiGallery = cdiGallery;
    galleryEl._cdiPhotoSwipe = cdiPhotoSwipe;
  });
}
document.addEventListener('DOMContentLoaded', () => {
  initCdiGallery();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      document.querySelectorAll('media-gallery').forEach((galleryEl) => {
        if (galleryEl._cdiGallery) {
          galleryEl._cdiGallery.destroy();
          galleryEl._cdiGallery = null;
          galleryEl._cdiPhotoSwipe = null;
        }
        galleryEl._cdiGalleryInit = false;
      });
      requestAnimationFrame(() => initCdiGallery());
    });
  }
});
